// Legal / policy content for GetInterviewed.
// Operator: Digital Nomads Asia and Rukesh Varan. Contact: privacy@getinterviewed.app
export const OPERATOR = "Digital Nomads Asia and Rukesh Varan";
export const CONTACT_EMAIL = "privacy@getinterviewed.app";
export const EFFECTIVE_DATE = "June 2026";

export const LEGAL = {
  terms: {
    slug: "terms",
    title: "Terms of Service",
    intro:
      `These Terms of Service ("Terms") govern your access to and use of GetInterviewed (the "Service"), ` +
      `operated by ${OPERATOR} ("we", "us", "our"). By creating an account or using the Service, you agree to be ` +
      `bound by these Terms, our Privacy Policy, Data Retention Policy and PDPA Statement. If you do not agree, do not use the Service.`,
    sections: [
      {
        heading: "1. The Service",
        body: [
          "GetInterviewed helps you tailor your CV to job descriptions, generate cover letters, assess ATS match, refine your CV by voice, track applications, and (where unlocked) access premium career-coaching features. Core résumé features are provided free of charge; the Career Journey feature is a paid add-on (see Payments).",
        ],
      },
      {
        heading: "2. Eligibility & your account",
        body: [
          "You must be at least 16 years old and able to form a binding contract to use the Service. You are responsible for the activity under your account and for keeping your sign-in secure. You agree to provide accurate information and to use the Service lawfully.",
        ],
      },
      {
        heading: "3. Your content",
        body: [
          "You retain ownership of the content you provide (including your CV, job descriptions and voice input). You grant us a limited licence to process that content solely to provide the Service to you (including sending it to our AI and infrastructure providers described in our Privacy Policy). You are responsible for ensuring your content is accurate and that you have the right to submit it.",
        ],
      },
      {
        heading: "4. Acceptable use",
        body: [
          "You agree not to: misuse the Service or interfere with its operation; attempt to access other users' data; upload unlawful, infringing or malicious content; scrape or overload the Service; or use it to generate false, misleading or fraudulent job-application material. We may suspend or terminate accounts that breach these Terms.",
        ],
      },
      {
        heading: "5. AI-generated output",
        body: [
          "The Service uses AI to generate suggestions, tailored CVs, cover letters, scores and coaching. Output may be inaccurate or incomplete, and ATS scores are estimates, not guarantees. You are responsible for reviewing and verifying all output before you rely on or submit it. We do not guarantee any particular outcome, including interviews or job offers.",
        ],
      },
      {
        heading: "6. Payments (Career Journey)",
        body: [
          "The Career Journey feature is available as a one-time lifetime unlock or a recurring monthly subscription, at the prices shown at checkout. Payments are processed by Stripe; by purchasing, you also agree to Stripe's terms. Subscriptions renew automatically until cancelled and you can cancel at any time, effective at the end of the current billing period.",
          "Voluntary donations (the “Support Us” tip jar) are non-refundable. For paid Career Journey purchases, contact " + CONTACT_EMAIL + " for any refund request; except where required by law, one-time unlocks are generally non-refundable once access has been used, and subscription fees already charged are non-refundable.",
        ],
      },
      {
        heading: "7. Intellectual property",
        body: [
          "The Service, including its software, design and branding, is owned by us or our licensors and is protected by law. These Terms do not grant you any right in our intellectual property except the limited right to use the Service as intended.",
        ],
      },
      {
        heading: "8. Third-party services",
        body: [
          "The Service relies on third parties (including Google, OpenAI, Emergent, Resend and Stripe). We are not responsible for third-party services, and your use of them may be subject to their own terms.",
        ],
      },
      {
        heading: "9. Disclaimers",
        body: [
          "The Service is provided \"as is\" and \"as available\", without warranties of any kind to the fullest extent permitted by law. We do not warrant that the Service will be uninterrupted, error-free or secure, or that any result will be achieved.",
        ],
      },
      {
        heading: "10. Limitation of liability",
        body: [
          "To the fullest extent permitted by law, we will not be liable for any indirect, incidental, special or consequential loss, or for loss of employment, income, data or goodwill. Our total aggregate liability arising out of or relating to the Service will not exceed the greater of the amount you paid us in the 12 months before the claim, or SGD 100. Nothing in these Terms excludes liability that cannot be excluded by law.",
        ],
      },
      {
        heading: "11. Termination",
        body: [
          "You may stop using the Service and delete your account at any time. We may suspend or terminate your access if you breach these Terms or if we discontinue the Service. Sections that by their nature should survive termination (including content licence limits, disclaimers, liability limits and governing law) will survive.",
        ],
      },
      {
        heading: "12. Changes to these Terms",
        body: [
          "We may update these Terms from time to time. If we make material changes, we will update the \"last updated\" date and, where appropriate, seek your renewed agreement. Your continued use of the Service after changes take effect constitutes acceptance.",
        ],
      },
      {
        heading: "13. Governing law",
        body: [
          "These Terms are governed by the laws of Singapore, and the courts of Singapore have exclusive jurisdiction, without prejudice to any mandatory consumer-protection rights you may have in your country of residence.",
        ],
      },
      {
        heading: "14. Contact",
        body: [
          `Questions about these Terms: ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },

  privacy: {
    slug: "privacy",
    title: "Privacy Policy",
    intro:
      `This Privacy Policy explains how ${OPERATOR} ("we", "us", "our"), the operator of GetInterviewed ` +
      `(the "Service"), collects, uses, discloses and protects your personal data. By creating an account or ` +
      `otherwise using the Service, you acknowledge that you have read and understood this Policy and consent ` +
      `to the collection, use and disclosure of your personal data as described here.`,
    sections: [
      {
        heading: "1. Who we are",
        body: [
          `The Service is operated by ${OPERATOR}. For any privacy-related matter, or to exercise your rights, ` +
            `contact our data protection contact at ${CONTACT_EMAIL}.`,
        ],
      },
      {
        heading: "2. Personal data we collect",
        body: [
          "We collect the following categories of personal data:",
          {
            list: [
              "Account data: your name, email address, profile picture and account identifier, provided through Google sign-in.",
              "Your CV data: the master CV you upload (file and the text extracted from it), and the tailored CVs, cover letters and ATS analyses we generate for you.",
              "Job application data: job descriptions you paste or job links you submit (and the content retrieved from them), plus the application records and statuses you track.",
              "Voice data: when you use “Talk to Refine Your CV”, the audio you record is transmitted for transcription. We do not retain the raw audio after transcription; the resulting text is handled as part of your CV data.",
              "Community content: comments, and the name and picture shown alongside them, that you choose to post.",
              "Payment data: if you make a voluntary donation, we record the transaction reference, amount, currency and status. We do not receive or store your full card details — payments are processed by Stripe.",
              "Technical data: session tokens, cookies, rate-limiting records, and server logs (such as IP address and request metadata) used to operate and secure the Service.",
            ],
          },
        ],
      },
      {
        heading: "3. How and why we use your data",
        body: [
          "We use your personal data to:",
          {
            list: [
              "provide the core Service — parsing your CV, tailoring it to a job description, generating cover letters, scoring ATS match and letting you download or email your application;",
              "authenticate you and keep your account secure;",
              "operate the voice refinement, community and application-tracking features;",
              "process voluntary donations and keep the associated financial records;",
              "maintain, protect and improve the Service, prevent abuse and enforce our terms; and",
              "comply with legal, accounting and regulatory obligations.",
            ],
          },
          "Where the law requires a lawful basis (for example, under the EU/UK GDPR), we rely on: your consent; the performance of our agreement to provide the Service to you; our legitimate interests in operating and securing the Service; and compliance with legal obligations.",
        ],
      },
      {
        heading: "4. Service providers and disclosure",
        body: [
          "We do not sell your personal data. We share it only with service providers who process it on our behalf and under contract, including:",
          {
            list: [
              "Google — for authentication;",
              "OpenAI (accessed via the Emergent platform) — to generate tailored CVs and cover letters, transcribe voice input and produce spoken audio;",
              "Emergent — for application hosting, integration and secure file/object storage;",
              "Resend — to send application emails on your instruction;",
              "Stripe — to process voluntary donations.",
            ],
          },
          "We may also disclose personal data where required by law, to protect our rights or the safety of users, or in connection with a business transfer.",
        ],
      },
      {
        heading: "5. International transfers",
        body: [
          "Some of our service providers process data outside your country, including outside Singapore, the EU and the UK. Where we transfer personal data across borders, we take steps to ensure it is protected to a standard comparable to that required under applicable law, including through contractual safeguards with our providers.",
        ],
      },
      {
        heading: "6. Retention",
        body: [
          "We keep personal data only for as long as necessary for the purposes described in this Policy. See our Data Retention Policy for specific periods and how to request deletion.",
        ],
      },
      {
        heading: "7. Your rights",
        body: [
          "Subject to applicable law, you may request access to, and correction of, your personal data, withdraw consent, or request deletion. If the EU/UK GDPR applies to you, you also have rights to erasure, portability, restriction and objection, and the right to lodge a complaint with a supervisory authority. See our PDPA Statement for how these rights apply in Singapore.",
          `To exercise any right, email ${CONTACT_EMAIL}. We will respond within the timeframes required by applicable law.`,
        ],
      },
      {
        heading: "8. Security",
        body: [
          "We apply reasonable technical and organisational measures to protect your personal data, including access controls, encryption in transit, per-user data isolation and rate limiting. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
        ],
      },
      {
        heading: "9. Children",
        body: [
          "The Service is not directed to individuals under 16, and we do not knowingly collect their personal data.",
        ],
      },
      {
        heading: "10. Changes",
        body: [
          "We may update this Policy from time to time. Material changes will be reflected by updating the “last updated” date, and your continued use of the Service after changes take effect constitutes acceptance of the updated Policy.",
        ],
      },
      {
        heading: "11. Contact",
        body: [
          `Questions or requests: ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },

  retention: {
    slug: "data-retention",
    title: "Data Retention Policy",
    intro:
      `This Data Retention Policy sets out how long ${OPERATOR} retains personal data collected through ` +
      `GetInterviewed, and how you can have it deleted. It forms part of, and should be read together with, our Privacy Policy.`,
    sections: [
      {
        heading: "1. Principle",
        body: [
          "We retain personal data only for as long as it is necessary to fulfil the purposes for which it was collected, to provide the Service to you, or to meet legal, tax and accounting obligations. When data is no longer needed, we delete or anonymise it.",
        ],
      },
      {
        heading: "2. Retention periods",
        body: [
          {
            list: [
              "Account data (name, email, picture, identifier): retained for as long as your account is active.",
              "Master CV, tailored CVs, cover letters, ATS analyses and application records: retained until you delete them, or until your account is deleted.",
              "Free CV Check uploads from visitors without an account: retained until the visitor deletes them with \"Delete my CV now\", or until they move into a new account.",
              "Voice recordings: the raw audio is not stored — it is processed transiently for transcription and then discarded. Any transcript you save becomes part of your CV data and follows the same retention as above.",
              "Generated audio (text-to-speech): streamed for playback and not stored on our servers.",
              "Community comments: retained until you (or we, where they breach our terms) delete them.",
              "Payment/donation records: retained for up to seven (7) years to meet tax and accounting requirements. Card details are never stored by us; they are held by Stripe under its own retention rules.",
              "Session tokens: expire automatically (currently seven days) and are removed on sign-out.",
              "Rate-limiting records: automatically expire after a short period (currently up to 48 hours).",
              "Server logs: retained only for a short period for security and troubleshooting.",
            ],
          },
        ],
      },
      {
        heading: "3. Deleting your data",
        body: [
          "You can delete individual applications and community comments at any time from within the Service. From Privacy & Data in your dashboard you can download a copy of your data, delete your master CV, or permanently delete your entire account and associated personal data yourself.",
          `Deletion takes effect immediately, except for data we are required to retain by law (such as payment and donation records kept for accounting purposes, which are retained without a link to your account) or that has been irreversibly anonymised. You can also email ${CONTACT_EMAIL} for help.`,
        ],
      },
      {
        heading: "4. Backups",
        body: [
          "Residual copies of deleted data may persist in routine backups for a limited period before being overwritten in the ordinary course of operations.",
        ],
      },
      {
        heading: "5. Changes",
        body: [
          "We may update this Policy from time to time; the “last updated” date reflects the current version.",
        ],
      },
    ],
  },

  pdpa: {
    slug: "pdpa",
    title: "PDPA Statement",
    intro:
      `This statement explains how ${OPERATOR} complies with Singapore's Personal Data Protection Act 2012 ` +
      `(the "PDPA") when handling personal data through GetInterviewed. A short note on the EU/UK GDPR appears at the end.`,
    sections: [
      {
        heading: "1. Our commitment",
        body: [
          "We are committed to managing personal data in accordance with the PDPA. This statement supplements our Privacy Policy and Data Retention Policy.",
        ],
      },
      {
        heading: "2. Consent, purpose and notification",
        body: [
          "We collect, use and disclose your personal data only for purposes that a reasonable person would consider appropriate in the circumstances and that we have notified to you — principally, to provide and operate the Service as described in our Privacy Policy. By using the Service you consent to such collection, use and disclosure. You may withdraw consent at any time (see below), though this may prevent us from continuing to provide the Service.",
        ],
      },
      {
        heading: "3. Data Protection Officer",
        body: [
          `We have designated a contact responsible for ensuring our compliance with the PDPA. You may reach our Data Protection Officer at ${CONTACT_EMAIL}.`,
        ],
      },
      {
        heading: "4. Access and correction",
        body: [
          "You have the right to request access to the personal data we hold about you and information about how it has been used or disclosed, and to request correction of any error or omission. We will respond to such requests as soon as reasonably possible and in accordance with the PDPA. We may charge a reasonable fee for access requests where permitted.",
        ],
      },
      {
        heading: "5. Withdrawing consent",
        body: [
          `You may withdraw your consent to our collection, use or disclosure of your personal data by contacting ${CONTACT_EMAIL}. On receiving your request, we will inform you of the likely consequences of withdrawal and cease the relevant processing within a reasonable time.`,
        ],
      },
      {
        heading: "6. Accuracy and protection",
        body: [
          "We make reasonable efforts to ensure personal data is accurate and complete where it is likely to be used to make a decision affecting you, or disclosed to another organisation. We protect personal data in our possession or control with reasonable security arrangements to prevent unauthorised access, collection, use, disclosure, copying, modification or disposal.",
        ],
      },
      {
        heading: "7. Retention limitation",
        body: [
          "We cease to retain personal data, or remove the means by which it can be associated with you, as soon as it is reasonable to assume that retention no longer serves the purpose for which it was collected and is no longer necessary for legal or business purposes. See our Data Retention Policy.",
        ],
      },
      {
        heading: "8. Transfer of personal data overseas",
        body: [
          "Where we transfer personal data outside Singapore (for example, to service providers such as OpenAI, Stripe, Resend, Google and Emergent), we take steps to ensure the recipient provides a standard of protection comparable to the PDPA, including through contractual arrangements.",
        ],
      },
      {
        heading: "9. Complaints",
        body: [
          `If you have a concern about how we handle your personal data, please contact us first at ${CONTACT_EMAIL} so we can address it. If you remain unsatisfied, you may lodge a complaint with the Personal Data Protection Commission (PDPC) of Singapore.`,
        ],
      },
      {
        heading: "10. EU / UK GDPR note",
        body: [
          "If you are located in the European Economic Area or the United Kingdom, the EU/UK General Data Protection Regulation may also apply. In that case, our lawful bases for processing are consent, performance of our agreement with you, our legitimate interests in operating and securing the Service, and compliance with legal obligations. You have the rights of access, rectification, erasure, restriction, portability and objection, and the right to lodge a complaint with your local supervisory authority (in the UK, the Information Commissioner's Office). To exercise these rights, contact " +
            CONTACT_EMAIL + ".",
        ],
      },
    ],
  },
};

export const LEGAL_LINKS = [
  { to: "/terms", label: "Terms of Service" },
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/data-retention", label: "Data Retention" },
  { to: "/pdpa", label: "PDPA Statement" },
];
